import type { ReactNode } from "react";
import { tokens } from "../theme/tokens";
import { tablePadding, tableSpacing } from "./tableSpacing";

// Tabla base paginada por cursor, reutilizable por todos los listados
// (SPEC-201 §4.1). Es deliberadamente genérica: cada módulo aporta columnas y
// filas, este componente solo resuelve grilla + paginación + estados.
// Reemplaza el patrón repetido "display: grid + COLS local" que
// components.md §3.3 marca como "candidato número uno a extraer".

export interface CursorTableColumn<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  width?: string;
  /**
   * La celda recorta su contenido por defecto (`hidden`), que es lo que evita
   * que un texto largo desborde su columna y empuje la grilla.
   *
   * `"visible"` es la excepción para una celda que dibuja algo que DEBE
   * salirse de su caja: hoy, el `Tooltip` de una acción o de un badge
   * (revisión en vivo 2026-09-22, puntos #10 y #16). Un tooltip está
   * posicionado en absoluto respecto de su disparador, así que con `hidden`
   * se recorta contra el borde de la celda y no se ve nada. Solo tiene
   * sentido en columnas cuyo contenido ya es de ancho acotado (un badge
   * `nowrap`, un botón de ícono); en una columna de texto libre reintroduce
   * el desborde que `hidden` evita.
   */
  overflow?: "hidden" | "visible";
}

export interface CursorPage {
  nextCursor: string | null;
  totalCount: number;
}

interface CursorTableProps<T> {
  columns: CursorTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  page: CursorPage;
  onLoadMore: () => void;
  isLoadingMore?: boolean;
  rowActions?: (row: T) => ReactNode;
  actionsHeader?: string;
  actionWidth?: string;
  actionGap?: number;
  columnGap?: string;
  headerPadding?: string;
  rowPadding?: string;
  headerFont?: string;
  rowFont?: string;
  borderRadius?: number;
  minWidth?: number | string;
  showFooter?: boolean;
}

export function CursorTable<T>({
  columns,
  rows,
  rowKey,
  page,
  onLoadMore,
  isLoadingMore,
  rowActions,
  actionsHeader = "Acciones",
  actionWidth = "auto",
  actionGap = tableSpacing.actionGap,
  columnGap = tableSpacing.columnGap,
  headerPadding = tablePadding("14px"),
  rowPadding = tablePadding("14px"),
  headerFont,
  rowFont,
  borderRadius = 18,
  minWidth,
  showFooter = true,
}: CursorTableProps<T>) {
  const templateColumns =
    columns.map((c) => c.width ?? "1fr").join(" ") +
    (rowActions ? ` ${actionWidth}` : "");

  return (
    <div
      style={{
        border: `1px solid ${tokens.surface.line}`,
        borderRadius,
        overflowX: "auto",
        overflowY: "hidden",
        background: tokens.surface.surface,
      }}
    >
      <div style={{ minWidth }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: templateColumns,
            columnGap,
            borderBottom: `1px solid ${tokens.surface.line}`,
            padding: headerPadding,
          }}
        >
          {columns.map((c) => (
            <div key={c.key} className="label" style={{ font: headerFont }}>
              {c.header}
            </div>
          ))}
          {rowActions ? (
            <div
              className="label"
              style={{
                textAlign: "right",
                font: headerFont,
              }}
            >
              {actionsHeader}
            </div>
          ) : null}
        </div>

        {rows.map((row) => (
          <div
            key={rowKey(row)}
            style={{
              display: "grid",
              gridTemplateColumns: templateColumns,
              columnGap,
              borderBottom: `1px solid ${tokens.surface.line}`,
              padding: rowPadding,
              alignItems: "center",
            }}
          >
            {columns.map((c) => (
              <div
                key={c.key}
                style={{
                  font: rowFont ?? `400 13px ${tokens.font.ui}`,
                  color: tokens.text.primary,
                  minWidth: 0,
                  overflow: c.overflow ?? "hidden",
                }}
              >
                {c.render(row)}
              </div>
            ))}
            {rowActions ? (
              <div
                style={{
                  display: "flex",
                  gap: actionGap,
                  justifyContent: "flex-end",
                  minWidth: 0,
                }}
              >
                {rowActions(row)}
              </div>
            ) : null}
          </div>
        ))}

        {showFooter ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: tablePadding("14px"),
            }}
          >
            <div
              style={{
                font: `400 12px ${tokens.font.ui}`,
                color: tokens.text.tertiary,
              }}
            >
              {page.totalCount}{" "}
              {page.totalCount === 1 ? "registro" : "registros"}
            </div>
            {page.nextCursor ? (
              <button
                type="button"
                className="btn"
                onClick={onLoadMore}
                disabled={isLoadingMore}
                style={{ font: `600 12px ${tokens.font.ui}` }}
              >
                {isLoadingMore ? "Cargando…" : "Cargar más"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
